import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Button,
    Card,
    CardContent,
    TextField,
    Radio,
    RadioGroup,
    FormControlLabel,
    FormControl
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { ArrowBack } from '@mui/icons-material';
import { getCampaignApprovedInfluencers, updateContentSection, getCampaignById, getCampaignFlowById } from '../services/api';
import { toast } from 'react-toastify';

function ContentVideoLive() {
    const { id, stageId } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState([]);
    const [campaign, setCampaign] = useState(null);
    const [campaignFlow, setCampaignFlow] = useState({});
    const [showSubmitButton, setShowSubmitButton] = useState(false);

    useEffect(() => {
        fetchCampaignFlowData();
        fetchCampaignData();
    }, []);

    useEffect(() => {
        if (campaign) {
            fetchApprovedInfluencers();
        }
    }, [campaign]);

    const fetchCampaignFlowData = async () => {
        try {
            const response = await getCampaignFlowById(stageId);
            const flowData = response.data.data;
            setCampaignFlow(flowData);
            setShowSubmitButton(flowData.status == 0);
        } catch (error) {
            console.error(error);
        }
    };

    const fetchCampaignData = async () => {
        try {
            const response = await getCampaignById(id);
            setCampaign(response.data.data);
        } catch (error) {
            console.error('Failed to fetch campaign:', error);
        }
    };

    const fetchApprovedInfluencers = async () => {
        setLoading(true);
        try {
            const response = await getCampaignApprovedInfluencers(id);
            const data = response.data.data || [];

            setRows(data.map(inf => {
                const contentData = campaign?.contentSection?.find(content => {
                    if (!content?.influencerId) return false;

                    const contentId = typeof content.influencerId === 'object'
                        ? content.influencerId._id
                        : content.influencerId;

                    return contentId?.toString() === inf._id?.toString();
                });

                return {
                    ...inf,
                    id: inf._id,
                    firstDraft: contentData?.contentFirstDraft === 1 ? 'yes' : '',
                    aiEval: contentData?.AIEvaluation === 1 ? 'yes' : '',
                    mgrApproval: contentData?.campaignManagerApproval === 1 ? 'yes' : '',
                    infMgrApproval: contentData?.influencerManagerApproval === 1 ? 'yes' : '',
                    link: contentData?.contentLink || ''
                };
            }));
        } catch (error) {
            console.error(error);
            toast.error('Failed to fetch approved influencers');
        } finally {
            setLoading(false);
        }
    };

    const handleRadioChange = (id, field, value) => {
        setRows(prevRows => prevRows.map(row =>
            row.id === id ? { ...row, [field]: value } : row
        ));
    };

    const handleLinkChange = (id, value) => {
        setRows(prevRows => prevRows.map(row =>
            row.id === id ? { ...row, link: value } : row
        ));
    };

    const handleSubmit = async () => {
        try {
            const contentData = rows.map(row => ({
                influencerId: row._id,
                contentFirstDraft: row.firstDraft === 'yes' ? 1 : 0,
                AIEvaluation: row.aiEval === 'yes' ? 1 : 0,
                campaignManagerApproval: row.mgrApproval === 'yes' ? 1 : 0,
                influencerManagerApproval: row.infMgrApproval === 'yes' ? 1 : 0,
                contentLink: row.link || null
            }));

            await updateContentSection(id, { contentData, stageStatus: 15, stageId });
            toast.success('Content status updated successfully');
            navigate(`/marketing/edit/${id}`);
        } catch (error) {
            console.error(error);
            toast.error('Failed to update content status');
        }
    };

    const renderRadioGroup = (params, field) => {
        return showSubmitButton ? (
            <FormControl component="fieldset" fullWidth>
                <RadioGroup
                    row
                    value={params.value}
                    onChange={(e) => handleRadioChange(params.id, field, e.target.value)}
                    sx={{ justifyContent: 'center' }}
                >
                    <FormControlLabel
                        value="yes"
                        control={<Radio size="small" />}
                        label="Y"
                        sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
                    />
                    <FormControlLabel
                        value="no"
                        control={<Radio size="small" />}
                        label="N"
                        sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
                    />
                </RadioGroup>
            </FormControl>
        ) : (
            <Typography variant="body2" color="text.secondary" align="center">
                {params.value == 'yes' ? 'Yes' : params.value == 0 ? 'No' : 'N/A'}
            </Typography>
        );
    };

    const columns = [
        {
            field: 'id',
            headerName: 'S.No',
            width: 70,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
                const index = rows.findIndex(row => row._id === params.row._id);
                return index + 1;
            }
        },
        {
            field: 'name',
            headerName: 'Influencer Name',
            width: 200,
            renderCell: (params) => (
                <Typography variant="body2" fontWeight="medium">{params.value}</Typography>
            )
        },
        {
            field: 'firstDraft',
            headerName: 'Content First Draft',
            width: 200,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => renderRadioGroup(params, 'firstDraft')
        },
        {
            field: 'aiEval',
            headerName: 'AI Evaluation',
            width: 180,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => renderRadioGroup(params, 'aiEval')
        },
        {
            field: 'mgrApproval',
            headerName: 'Campaign Manager Approval',
            width: 250,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => renderRadioGroup(params, 'mgrApproval')
        },
        {
            field: 'infMgrApproval',
            headerName: 'Influencer Mgr Approval',
            width: 220,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => renderRadioGroup(params, 'infMgrApproval')
        },
        {
            field: 'link',
            headerName: 'Content Live Link',
            width: 250,
            renderCell: (params) => {
                return showSubmitButton ? (
                    <TextField
                        size="small"
                        fullWidth
                        placeholder="Enter URL"
                        value={params.value}
                        onChange={(e) => handleLinkChange(params.id, e.target.value)}
                        sx={{
                            bgcolor: 'white',
                            '& .MuiOutlinedInput-root': { borderRadius: 1 }
                        }}
                    />
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        {params.value || 'N/A'}
                    </Typography>
                );
            }
        }
    ];

    return (
        <Box sx={{ p: 4, bgcolor: '#f8fafc', minHeight: '100vh' }}>
            {/* Header */}
            <Box sx={{ mb: 4 }}>
                <Button
                    startIcon={<ArrowBack />}
                    onClick={() => navigate(`/marketing/edit/${id}`)}
                    sx={{ mb: 2, color: 'text.secondary' }}
                >
                    Back
                </Button>
                <Typography variant="h5" fontWeight="bold" sx={{ color: '#1a1a1a' }}>
                    Content & Video Live
                </Typography>
            </Box>

            <Card sx={{ boxShadow: '0 2px 10px rgba(0,0,0,0.05)', borderRadius: 2 }}>
                <CardContent sx={{ p: 0 }}>
                    <DataGrid
                        rows={rows}
                        columns={columns}
                        getRowId={(row) => row._id}
                        autoHeight
                        disableSelectionOnClick
                        hideFooter
                        loading={loading}
                        rowHeight={70} // Increased height for radio buttons
                        sx={{
                            border: 'none',
                            '& .MuiDataGrid-columnHeaders': {
                                backgroundColor: '#f8f9fa',
                                color: '#666',
                                fontSize: '13px',
                                textTransform: 'uppercase',
                                borderBottom: '1px solid #e0e0e0',
                                fontWeight: 600
                            },
                            '& .MuiDataGrid-cell': {
                                borderBottom: '1px solid #f0f0f0',
                                py: 1
                            },
                            '& .MuiDataGrid-row:hover': {
                                backgroundColor: '#fafafa'
                            }
                        }}
                    />
                </CardContent>
            </Card>

            {showSubmitButton && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSubmit}
                        sx={{ px: 4, py: 1 }}
                    >
                        Submit
                    </Button>
                </Box>
            )}
        </Box>
    );
}

export default ContentVideoLive;
